// CRM-NANCOZI — Journal d'Audit (Admin uniquement)
import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAuditLogs, fetchAuditResume } from '../../store/slices/auditSlice';
import {
  Search, ChevronLeft, ChevronRight, Eye,
  ShieldCheck, AlertTriangle, Activity, LogIn,
  BarChart2, List,
} from 'lucide-react';

/* ── Constantes ── */
const ACTIONS = {
  creation:     { label: 'Création',          badge: 'vert'   },
  modification: { label: 'Modification',       badge: 'bleu'   },
  suppression:  { label: 'Suppression',        badge: 'rouge'  },
  consultation: { label: 'Consultation',       badge: 'gris'   },
  connexion:    { label: 'Connexion',          badge: 'orange' },
  export:       { label: 'Export',             badge: 'bleu'   },
  envoi_notif:  { label: 'Envoi notification', badge: 'orange' },
};
const ENTITES = [
  'CLIENT','COACHING','RENOUVELLEMENT','FORMATION','INSCRIPTION',
  'UTILISATEUR','NOTIFICATION','DOCUMENT',
];
const PAGE_SIZE = 25;

const fmtDT = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('fr-FR') + ' ' + dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};
const fmtN = (n) => Number(n || 0).toLocaleString('fr-FR');

/* ══════════════════════════════════════════════════
   Modal — Détail d'un log
══════════════════════════════════════════════════ */
const CHAMP_LABELS = {
  id: 'ID', sgi: 'SGI', prix: 'Prix (FCFA)', client: 'Client ID',
  client_nom: 'Client', statut: 'Statut', domaine: 'Domaine',
  date_debut: 'Date début', date_fin: 'Date fin', duree_mois: 'Durée (mois)',
  type_coaching: 'Type coaching', montant_paye: 'Montant payé (FCFA)',
  statut_paiement: 'Statut paiement', commercial: 'Commercial ID',
  created_at: 'Créé le', seances: 'Séances', nb_seances: 'Nb séances',
  nom_util: 'Nom', prenom_util: 'Prénom', email: 'Email',
  telephone: 'Téléphone', role: 'Rôle', intitule: 'Intitulé',
  code_formation: 'Code', description: 'Description',
  nom_cli: 'Nom client', prenom_cli: 'Prénom client',
  date_naissance: 'Date naissance', numero_piece: 'N° pièce',
};

function renderVal(k, v) {
  if (v === null || v === undefined) return <span style={{ color: '#9CA3AF' }}>—</span>;
  if (typeof v === 'boolean') return v ? 'Oui' : 'Non';
  if (Array.isArray(v)) return v.length === 0
    ? <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>vide</span>
    : <span style={{ fontSize: 10, color: '#6B7280' }}>[{v.length} élément(s)]</span>;
  if (typeof v === 'object')
    return <pre style={{ fontSize: 10, margin: 0, color: '#6B7280', whiteSpace: 'pre-wrap' }}>{JSON.stringify(v, null, 2)}</pre>;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v))
    return new Date(v).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v))
    return new Date(v + 'T00:00:00').toLocaleDateString('fr-FR');
  if ((k === 'prix' || k === 'montant_paye') && !isNaN(Number(v)))
    return Number(v).toLocaleString('fr-FR') + ' FCFA';
  return String(v);
}

function RenderData({ obj }) {
  if (!obj) return <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>—</span>;
  const entries = Object.entries(obj);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px',
      background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6,
      padding: '10px 14px', maxHeight: 300, overflowY: 'auto' }}>
      {entries.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {CHAMP_LABELS[k] || k}
          </span>
          <span style={{ fontSize: 12, color: '#1F2937', wordBreak: 'break-word' }}>
            {renderVal(k, v)}
          </span>
        </div>
      ))}
    </div>
  );
}

function ModalDetail({ log, onClose }) {
  const [tab, setTab] = useState(log.valeur_avant ? 'avant' : 'apres');

  const titre = () => {
    const data = log.valeur_apres || log.valeur_avant;
    const ctx = data?.client_nom || data?.nom_util
      ? ` — ${data.prenom_util || ''} ${data.client_nom || data.nom_util || ''}`.trim()
      : data?.intitule ? ` — ${data.intitule}` : '';
    return `${log.entite}${ctx}`;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: 660 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Détail — {titre()}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            {[
              ['Utilisateur', log.utilisateur_nom || 'Système'],
              ['Action',      ACTIONS[log.action]?.label || log.action],
              ['Résultat',    log.resultat === 'succes' ? '✅ Succès' : '❌ Échec'],
              ['Date / Heure', fmtDT(log.date_heure)],
              ['IP',          log.ip_adresse || '—'],
              ['Entité',      log.entite],
            ].map(([k, v]) => (
              <div key={k} style={{ background: '#F9FAFB', borderRadius: 6, padding: '8px 10px' }}>
                <div style={{ fontSize: 10, color: '#9CA3AF', marginBottom: 2 }}>{k}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#1F2937' }}>{v}</div>
              </div>
            ))}
          </div>
          {log.detail && (
            <div>
              <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4 }}>Détail</div>
              <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
                {log.detail}
              </div>
            </div>
          )}
          {(log.valeur_avant || log.valeur_apres) && (
            <div>
              <div style={{ display: 'flex', gap: 0, marginBottom: 8, borderBottom: '2px solid #E5E7EB' }}>
                {log.valeur_avant && (
                  <button onClick={() => setTab('avant')}
                    style={{ padding: '6px 16px', fontSize: 12, fontWeight: 600, border: 'none', background: 'none', cursor: 'pointer',
                      borderBottom: tab === 'avant' ? '2px solid #C00000' : '2px solid transparent',
                      color: tab === 'avant' ? '#C00000' : '#6B7280', marginBottom: -2 }}>Avant</button>
                )}
                {log.valeur_apres && (
                  <button onClick={() => setTab('apres')}
                    style={{ padding: '6px 16px', fontSize: 12, fontWeight: 600, border: 'none', background: 'none', cursor: 'pointer',
                      borderBottom: tab === 'apres' ? '2px solid #1E7145' : '2px solid transparent',
                      color: tab === 'apres' ? '#1E7145' : '#6B7280', marginBottom: -2 }}>Après</button>
                )}
              </div>
              {tab === 'avant' && <RenderData obj={log.valeur_avant} />}
              {tab === 'apres' && <RenderData obj={log.valeur_apres} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   Onglet Résumé par utilisateur
══════════════════════════════════════════════════ */
function TabResume({ resume, loadingResume, onVoirDetail }) {
  const [search, setSearch] = useState('');

  const filtered = (resume || []).filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    const nom = `${r.utilisateur__prenom_util || ''} ${r.utilisateur__nom_util || ''}`.toLowerCase();
    return nom.includes(q) || (r.utilisateur__email || '').toLowerCase().includes(q);
  });

  /* Totaux globaux */
  const totaux = resume.reduce((acc, r) => ({
    total:         acc.total         + (r.total         || 0),
    connexions:    acc.connexions    + (r.connexions    || 0),
    echecs:        acc.echecs        + (r.echecs        || 0),
    suppressions:  acc.suppressions  + (r.suppressions  || 0),
  }), { total: 0, connexions: 0, echecs: 0, suppressions: 0 });

  const bar = (val, max, color) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ flex: 1, height: 6, background: '#F3F4F6', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${max ? Math.round((val / max) * 100) : 0}%`, height: '100%', background: color, borderRadius: 3, transition: 'width .3s' }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color, minWidth: 28, textAlign: 'right' }}>{fmtN(val)}</span>
    </div>
  );

  const maxTotal = Math.max(...(resume.map(r => r.total || 0)), 1);

  return (
    <div>
      {/* KPIs globaux */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
        {[
          { label: 'Actions totales',   value: fmtN(totaux.total),       color: '#1F4E79', icon: Activity     },
          { label: 'Connexions',        value: fmtN(totaux.connexions),   color: '#ED7D31', icon: LogIn        },
          { label: 'Utilisateurs actifs', value: resume.length,           color: '#1E7145', icon: ShieldCheck  },
          { label: 'Échecs totaux',     value: fmtN(totaux.echecs),       color: '#C00000', icon: AlertTriangle},
        ].map(({ label, value, color, icon: Icon }) => (
          <div key={label} className="card" style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#1F2937' }}>{value}</div>
            </div>
            <div style={{ background: color + '18', borderRadius: 10, padding: 10 }}>
              <Icon size={22} color={color} />
            </div>
          </div>
        ))}
      </div>

      {/* Barre de recherche */}
      <div style={{ marginBottom: 12 }}>
        <div className="search-bar" style={{ maxWidth: 320 }}>
          <Search size={14} />
          <input placeholder="Nom, email…" value={search}
            onChange={e => setSearch(e.target.value)} style={{ fontSize: 12 }} />
        </div>
      </div>

      {/* Tableau résumé */}
      <div className="table-container">
        {loadingResume ? (
          <div className="spinner"><div className="spinner-ring" /> Chargement…</div>
        ) : (
          <table style={{ width: '100%', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 34   }} /> {/* N° */}
              <col style={{ width: '22%' }} /> {/* Utilisateur */}
              <col style={{ width: 60   }} /> {/* Rôle */}
              <col style={{ width: '24%' }} /> {/* Total (barre) */}
              <col style={{ width: 80   }} /> {/* Créations */}
              <col style={{ width: 80   }} /> {/* Modifs */}
              <col style={{ width: 80   }} /> {/* Connexions */}
              <col style={{ width: 80   }} /> {/* Exports */}
              <col style={{ width: 80   }} /> {/* Suppressions */}
              <col style={{ width: 60   }} /> {/* Échecs */}
              <col style={{ width: 120  }} /> {/* Dernière action */}
              <col style={{ width: 52   }} /> {/* Détails */}
            </colgroup>
            <thead>
              <tr>
                <th style={{ fontSize: 11 }}>N°</th>
                <th style={{ fontSize: 11 }}>Utilisateur</th>
                <th style={{ fontSize: 11 }}>Rôle</th>
                <th style={{ fontSize: 11 }}>Total actions</th>
                <th style={{ fontSize: 11 }}>Créations</th>
                <th style={{ fontSize: 11 }}>Modifs</th>
                <th style={{ fontSize: 11 }}>Connexions</th>
                <th style={{ fontSize: 11 }}>Exports</th>
                <th style={{ fontSize: 11 }}>Suppres.</th>
                <th style={{ fontSize: 11 }}>Échecs</th>
                <th style={{ fontSize: 11 }}>Dernière action</th>
                <th style={{ fontSize: 11 }}>Logs</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={12} style={{ textAlign: 'center', padding: 32, color: '#6B7280' }}>
                  {search ? 'Aucun résultat' : 'Aucune donnée d\'audit'}
                </td></tr>
              ) : filtered.map((r, i) => (
                <tr key={r.utilisateur || i}
                  style={{ background: r.echecs > 0 ? '#FFFBEB' : undefined }}>
                  <td style={{ fontSize: 11, fontWeight: 700, color: '#1F4E79', padding: '8px 6px' }}>{i + 1}</td>
                  <td style={{ padding: '8px 6px', overflow: 'hidden' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.utilisateur__prenom_util} {r.utilisateur__nom_util}
                    </div>
                    <div style={{ fontSize: 10, color: '#6B7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.utilisateur__email}
                    </div>
                  </td>
                  <td style={{ padding: '8px 4px' }}>
                    <span className={`badge badge-${r.utilisateur__role === 'administrateur' ? 'rouge' : 'bleu'}`} style={{ fontSize: 9 }}>
                      {r.utilisateur__role === 'administrateur' ? 'Admin' : 'Commercial'}
                    </span>
                  </td>
                  <td style={{ padding: '8px 8px 8px 6px' }}>
                    {bar(r.total, maxTotal, '#1F4E79')}
                  </td>
                  <td style={{ fontSize: 11, padding: '8px 6px', textAlign: 'center', fontWeight: 600, color: '#1E7145' }}>
                    {fmtN(r.creations)}
                  </td>
                  <td style={{ fontSize: 11, padding: '8px 6px', textAlign: 'center', color: '#2E75B6' }}>
                    {fmtN(r.modifications)}
                  </td>
                  <td style={{ fontSize: 11, padding: '8px 6px', textAlign: 'center', color: '#ED7D31' }}>
                    {fmtN(r.connexions)}
                  </td>
                  <td style={{ fontSize: 11, padding: '8px 6px', textAlign: 'center', color: '#6B7280' }}>
                    {fmtN(r.exports)}
                  </td>
                  <td style={{ fontSize: 11, padding: '8px 6px', textAlign: 'center', color: r.suppressions > 0 ? '#C00000' : '#6B7280', fontWeight: r.suppressions > 0 ? 700 : 400 }}>
                    {fmtN(r.suppressions)}
                  </td>
                  <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                    {r.echecs > 0
                      ? <span className="badge badge-rouge" style={{ fontSize: 10 }}>{fmtN(r.echecs)}</span>
                      : <span style={{ fontSize: 11, color: '#9CA3AF' }}>0</span>}
                  </td>
                  <td style={{ fontSize: 10, padding: '8px 6px', color: '#6B7280' }}>
                    {fmtDT(r.derniere_action)}
                  </td>
                  <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                    <button className="header-btn btn-secondary btn-sm btn-icon"
                      onClick={() => onVoirDetail(r.utilisateur)}
                      title="Voir les logs détaillés">
                      <List size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   Onglet Logs détaillés
══════════════════════════════════════════════════ */
function TabLogs({ filtreUtilisateur, onClearUtilisateur }) {
  const dispatch = useDispatch();
  const { logs, count, next, previous, loading } = useSelector(s => s.audit);

  const [search,    setSearch]    = useState('');
  const [action,    setAction]    = useState('');
  const [entite,    setEntite]    = useState('');
  const [resultat,  setResultat]  = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin,   setDateFin]   = useState('');
  const [page,      setPage]      = useState(1);
  const [detail,    setDetail]    = useState(null);

  const load = useCallback(() => {
    const params = { page };
    if (search)           params.search    = search;
    if (action)           params.action    = action;
    if (entite)           params.entite    = entite;
    if (resultat)         params.resultat  = resultat;
    if (dateDebut)        params.date_heure__date__gte = dateDebut;
    if (dateFin)          params.date_heure__date__lte = dateFin;
    if (filtreUtilisateur) params.utilisateur = filtreUtilisateur;
    dispatch(fetchAuditLogs(params));
  }, [dispatch, page, search, action, entite, resultat, dateDebut, dateFin, filtreUtilisateur]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, action, entite, resultat, dateDebut, dateFin, filtreUtilisateur]);

  const totalPages = Math.ceil(count / PAGE_SIZE) || 1;
  const hasFiltres = search || action || entite || resultat || dateDebut || dateFin;

  const reset = () => {
    setSearch(''); setAction(''); setEntite('');
    setResultat(''); setDateDebut(''); setDateFin('');
  };

  return (
    <div>
      {/* Bandeau utilisateur filtré */}
      {filtreUtilisateur && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
          background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '8px 14px' }}>
          <span style={{ fontSize: 12, color: '#1D4ED8', fontWeight: 600 }}>
            Logs filtrés pour un utilisateur spécifique
          </span>
          <button onClick={onClearUtilisateur}
            style={{ fontSize: 11, color: '#C00000', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
            Afficher tous
          </button>
        </div>
      )}

      {/* Barre de filtres */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        <div className="search-bar" style={{ flex: '1 1 180px', minWidth: 160 }}>
          <Search size={14} />
          <input placeholder="Utilisateur, entité, détail…"
            value={search} onChange={e => setSearch(e.target.value)} style={{ fontSize: 12 }} />
        </div>
        <select value={action} onChange={e => setAction(e.target.value)}
          style={{ height: 36, padding: '0 8px', fontSize: 12, border: '1px solid #D1D5DB', borderRadius: 8, background: action ? '#EFF6FF' : 'white', color: '#1F2937', minWidth: 140 }}>
          <option value="">Toutes les actions</option>
          {Object.entries(ACTIONS).map(([v, { label }]) => <option key={v} value={v}>{label}</option>)}
        </select>
        <select value={entite} onChange={e => setEntite(e.target.value)}
          style={{ height: 36, padding: '0 8px', fontSize: 12, border: '1px solid #D1D5DB', borderRadius: 8, background: entite ? '#EFF6FF' : 'white', color: '#1F2937', minWidth: 130 }}>
          <option value="">Toutes les entités</option>
          {ENTITES.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={resultat} onChange={e => setResultat(e.target.value)}
          style={{ height: 36, padding: '0 8px', fontSize: 12, border: '1px solid #D1D5DB', borderRadius: 8, background: resultat ? '#EFF6FF' : 'white', color: '#1F2937', minWidth: 110 }}>
          <option value="">Tous résultats</option>
          <option value="succes">✅ Succès</option>
          <option value="echec">❌ Échec</option>
        </select>
        <div style={{ width: 1, height: 28, background: '#E5E7EB', flexShrink: 0 }} />
        <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} title="Date début"
          style={{ height: 36, padding: '0 8px', fontSize: 12, border: '1px solid #D1D5DB', borderRadius: 8, color: '#1F2937', background: dateDebut ? '#EFF6FF' : 'white' }} />
        <span style={{ fontSize: 12, color: '#9CA3AF' }}>→</span>
        <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} title="Date fin"
          style={{ height: 36, padding: '0 8px', fontSize: 12, border: '1px solid #D1D5DB', borderRadius: 8, color: '#1F2937', background: dateFin ? '#EFF6FF' : 'white' }} />
        {hasFiltres && (
          <button onClick={reset}
            style={{ fontSize: 11, color: '#C00000', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap' }}>
            Effacer filtres
          </button>
        )}
      </div>

      <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>
        {fmtN(count)} entrée(s) · page {page}/{totalPages}
      </div>

      {/* Tableau logs */}
      <div className="table-container">
        {loading ? (
          <div className="spinner"><div className="spinner-ring" /> Chargement…</div>
        ) : (
          <>
            <table style={{ width: '100%', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 44   }} />
                <col style={{ width: 130  }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: 110  }} />
                <col style={{ width: 110  }} />
                <col style={{ width: 55   }} />
                <col style={{ width: 105  }} />
                <col style={{ width: 72   }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: 46   }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={{ fontSize: 11 }}>N°</th>
                  <th style={{ fontSize: 11 }}>Date / Heure</th>
                  <th style={{ fontSize: 11 }}>Utilisateur</th>
                  <th style={{ fontSize: 11 }}>Action</th>
                  <th style={{ fontSize: 11 }}>Entité</th>
                  <th style={{ fontSize: 11 }}>ID</th>
                  <th style={{ fontSize: 11 }}>IP</th>
                  <th style={{ fontSize: 11 }}>Résultat</th>
                  <th style={{ fontSize: 11 }}>Détail</th>
                  <th style={{ fontSize: 11 }}></th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>
                    Aucune entrée trouvée
                  </td></tr>
                ) : logs.map((log, i) => {
                  const act     = ACTIONS[log.action] || { label: log.action, badge: 'gris' };
                  const isEchec = log.resultat === 'echec';
                  return (
                    <tr key={log.id} style={{ background: isEchec ? '#FFF5F5' : undefined, cursor: 'pointer' }}
                      onClick={() => setDetail(log)}>
                      <td style={{ fontSize: 11, fontWeight: 700, color: '#1F4E79', padding: '7px 6px' }}>
                        {(page - 1) * PAGE_SIZE + i + 1}
                      </td>
                      <td style={{ fontSize: 11, padding: '7px 6px', whiteSpace: 'nowrap' }}>
                        {fmtDT(log.date_heure)}
                      </td>
                      <td style={{ padding: '7px 6px', overflow: 'hidden' }}>
                        <div style={{ fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {log.utilisateur_nom || <span style={{ color: '#9CA3AF' }}>Système</span>}
                        </div>
                      </td>
                      <td style={{ padding: '7px 4px' }}>
                        <span className={`badge badge-${act.badge}`} style={{ fontSize: 10 }}>{act.label}</span>
                      </td>
                      <td style={{ fontSize: 11, padding: '7px 6px', fontWeight: 700, color: '#1F4E79' }}>
                        {log.entite}
                      </td>
                      <td style={{ fontSize: 11, padding: '7px 6px', color: '#6B7280', textAlign: 'center' }}>
                        {log.id_enregistrement ?? '—'}
                      </td>
                      <td style={{ fontSize: 10, padding: '7px 6px', color: '#6B7280', fontFamily: 'monospace', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                        {log.ip_adresse || '—'}
                      </td>
                      <td style={{ padding: '7px 4px' }}>
                        <span className={`badge badge-${isEchec ? 'rouge' : 'vert'}`} style={{ fontSize: 10 }}>
                          {isEchec ? '✗ Échec' : '✓ OK'}
                        </span>
                      </td>
                      <td style={{ fontSize: 11, padding: '7px 6px', color: '#6B7280', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                        {log.detail || '—'}
                      </td>
                      <td style={{ padding: '7px 4px', textAlign: 'center' }}>
                        <button className="header-btn btn-secondary btn-sm btn-icon"
                          onClick={e => { e.stopPropagation(); setDetail(log); }} title="Voir le détail">
                          <Eye size={12} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, padding: '14px 0', borderTop: '1px solid #F3F4F6' }}>
                <button className="header-btn btn-secondary btn-sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={!previous || loading}
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <ChevronLeft size={14} /> Précédent
                </button>
                <span style={{ fontSize: 12, color: '#6B7280' }}>
                  Page <strong>{page}</strong> / {totalPages}  ({fmtN(count)} entrées)
                </span>
                <button className="header-btn btn-secondary btn-sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={!next || loading}
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  Suivant <ChevronRight size={14} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {detail && <ModalDetail log={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   Page principale
══════════════════════════════════════════════════ */
export default function AuditPage() {
  const dispatch = useDispatch();
  const { resume, loadingResume } = useSelector(s => s.audit);

  const [tab,              setTab]              = useState('resume');
  const [filtreUtilisateur, setFiltreUtilisateur] = useState(null);

  useEffect(() => {
    dispatch(fetchAuditResume());
  }, [dispatch]);

  const handleVoirDetail = (userId) => {
    setFiltreUtilisateur(userId);
    setTab('logs');
  };

  return (
    <div className="page-list">
      {/* En-tête */}
      <div style={{ marginBottom: 16 }}>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #E5E7EB' }}>
          {[
            { key: 'resume', label: 'Résumé par utilisateur', icon: BarChart2 },
            { key: 'logs',   label: 'Logs détaillés',          icon: List      },
          ].map(({ key, label, icon: Icon }) => (
            <button key={key}
              onClick={() => { setTab(key); if (key === 'resume') dispatch(fetchAuditResume()); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '9px 20px', fontSize: 13, fontWeight: 600,
                border: 'none', background: 'none', cursor: 'pointer', marginBottom: -2,
                borderBottom: tab === key ? '2px solid #1F4E79' : '2px solid transparent',
                color: tab === key ? '#1F4E79' : '#6B7280',
              }}>
              <Icon size={15} /> {label}
              {key === 'logs' && filtreUtilisateur && (
                <span className="badge badge-orange" style={{ fontSize: 9, marginLeft: 4 }}>filtré</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {tab === 'resume' && (
        <TabResume
          resume={resume}
          loadingResume={loadingResume}
          onVoirDetail={handleVoirDetail}
        />
      )}
      {tab === 'logs' && (
        <TabLogs
          filtreUtilisateur={filtreUtilisateur}
          onClearUtilisateur={() => setFiltreUtilisateur(null)}
        />
      )}
    </div>
  );
}
